import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { getApiErrorMessage } from '../../utils/apiError';

// Initial state
const initialState = {
  users: [],
  currentUser: null,
  isLoading: false,
  error: null,
};

// Async thunks for users
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users');
      const users = response.data.data.map(user => ({
        ...user,
        roles: user.roles ? user.roles.filter(role =>
          role.id && Number.isInteger(Number(role.id)) && Number(role.id) > 0
        ) : []
      }));
      return users;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch users'));
    }
  }
);

export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/users/${userId}`);
      const user = {
        ...response.data.data,
        roles: response.data.data.roles ? response.data.data.roles.filter(role =>
          role.id && Number.isInteger(Number(role.id)) && Number(role.id) > 0
        ) : []
      };
      return user;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch user'));
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/users', userData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create user'));
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update user'));
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await api.delete(`/users/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete user'));
    }
  }
);

export const assignRolesToUser = createAsyncThunk(
  'users/assignRolesToUser',
  async ({ userId, roles }, { rejectWithValue }) => {
    try {
      const filteredRoles = roles.filter(id => id && Number.isInteger(Number(id)) && Number(id) > 0);
      const response = await api.post(`/users/${userId}/assign-roles`, {
        roles: filteredRoles
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to assign roles'));
    }
  }
);

// Users slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch single user
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update user
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter(user => user.id !== action.payload);
        if (state.currentUser && state.currentUser.id === action.payload) {
          state.currentUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Assign roles to user
      .addCase(assignRolesToUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignRolesToUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(assignRolesToUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentUser, clearCurrentUser } = usersSlice.actions;
export default usersSlice.reducer;